import { Link, useParams } from "react-router-dom";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, ArrowUpRight, BookOpen, ExternalLink, Loader2, ShoppingCart } from "lucide-react";
import api from "@/lib/api";
import { startCheckout } from "@/lib/checkout";
import { T } from "@/constants/testIds";
import { FadeUp, Eyebrow } from "@/components/motion";
import Seo from "@/components/Seo";
import TiltCover from "@/components/TiltCover";
import Trailer from "@/components/Trailer";

export default function BookDetail() {
  const { slug } = useParams();
  const [buying, setBuying] = useState(false);
  const { data: book, isLoading, isError } = useQuery({
    queryKey: ["book", slug],
    queryFn: async () => (await api.get(`/books/${slug}`)).data,
  });
  const { data: allBooks } = useQuery({
    queryKey: ["books"],
    queryFn: async () => (await api.get("/books")).data,
  });
  const others = (allBooks || []).filter((b) => b.slug !== slug);

  if (isLoading) {
    return (
      <div className="flex min-h-[70vh] items-center justify-center pt-24 font-mono text-xs uppercase tracking-[0.3em] text-mute2">
        Opening book…
      </div>
    );
  }
  if (isError || !book) {
    return (
      <div className="flex min-h-[70vh] flex-col items-center justify-center gap-6 pt-24 text-center">
        <p className="font-serif text-4xl font-light text-ink">This book slipped out of the archive.</p>
        <Link
          to="/books"
          className="font-mono text-[11px] uppercase tracking-[0.24em] text-cyanx"
          data-testid="book-detail-back-link"
        >
          Back to the catalog
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1440px] px-5 pb-28 pt-28 sm:px-8 lg:px-14 lg:pt-36">
      <Seo
        title={`${book.title} — ${book.direct_price_label || book.price_label} | Isabella MacLeod`}
        description={`${book.tagline} ${book.synopsis.split("\n\n")[0].slice(0, 130)}…`}
        path={`/books/${book.slug}`}
      />
      <Link
        to="/books"
        data-testid="book-detail-back-link"
        className="group inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.24em] text-mute2 transition-colors hover:text-cyanx"
      >
        <ArrowLeft size={14} className="transition-transform duration-300 group-hover:-translate-x-1" />
        All books
      </Link>

      <div className="mt-12 grid gap-14 lg:grid-cols-12 lg:gap-20">
        <FadeUp className="lg:col-span-5">
          <div className="sticky top-28 mx-auto max-w-[420px]">
            <TiltCover src={book.cover} alt={`${book.title} — cover art`} />
          </div>
        </FadeUp>

        <div className="lg:col-span-7">
          <FadeUp>
            <Eyebrow className="mb-6">
              Book {String(book.series_order).padStart(2, "0")} — {book.year}
            </Eyebrow>
            <h1 className="font-serif text-5xl font-light leading-[1.02] tracking-tight text-ink sm:text-6xl lg:text-7xl">
              {book.title}
            </h1>
            <p className="mt-4 text-sm uppercase tracking-[0.2em] text-mute2">{book.subtitle}</p>
            {book.award && (
              <p className="mt-5 inline-block rounded-full border border-ember/40 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-ember">
                {book.award}
              </p>
            )}

            <div className="mt-10 flex flex-wrap items-baseline gap-4">
              <span className="font-serif text-4xl font-light italic text-cyanx">{book.price_label}</span>
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-mute2">
                {book.price_note}
              </span>
            </div>

            <div className="mt-7 flex flex-wrap gap-4">
              {book.stripe_lookup_key && (
                <button
                  onClick={async () => {
                    try {
                      setBuying(true);
                      await startCheckout(book.stripe_lookup_key);
                    } catch {
                      setBuying(false);
                      toast.error("Could not start checkout. Please try again.");
                    }
                  }}
                  disabled={buying}
                  data-testid={`buy-direct-${book.slug}`}
                  className="group flex items-center gap-2 rounded-full bg-cyanx px-7 py-4 font-mono text-[11px] uppercase tracking-[0.22em] text-void transition-all duration-300 hover:brightness-110 disabled:opacity-60"
                >
                  {buying ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <>
                      <ShoppingCart size={14} />
                      Buy direct — {book.direct_price_label}
                    </>
                  )}
                </button>
              )}
              <a
                href={book.amazon_com}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={T.buyAmazonCom(book.slug)}
                className="group flex items-center gap-2 rounded-full bg-ink px-7 py-4 font-mono text-[11px] uppercase tracking-[0.22em] text-void transition-colors duration-300 hover:bg-cyanx"
              >
                Buy on Amazon.com
                <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </a>
              <a
                href={book.amazon_ca}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={T.buyAmazonCa(book.slug)}
                className="group flex items-center gap-2 rounded-full border border-white/15 px-7 py-4 font-mono text-[11px] uppercase tracking-[0.22em] text-ink transition-all duration-300 hover:border-cyanx/60 hover:text-cyanx"
              >
                Buy on Amazon.ca
                <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </a>
            </div>
            <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-mute2">
              Kindle edition · Paperback via Amazon — prices in USD &amp; CAD shown at checkout
            </p>

            <div className="mt-12 border-t border-white/[0.07] pt-10">
              <h2 className="mb-6 font-mono text-[11px] uppercase tracking-[0.3em] text-cyanx">Synopsis</h2>
              <div className="space-y-5">
                {book.synopsis.split("\n\n").map((p, i) => (
                  <p key={i} className="max-w-2xl text-base font-light leading-relaxed text-dim sm:text-lg">
                    {p}
                  </p>
                ))}
              </div>
            </div>

            {book.excerpt && (
              <div className="mt-12 border-t border-white/[0.07] pt-10">
                <h2 className="mb-6 flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.3em] text-cyanx">
                  <BookOpen size={14} /> Sample chapter
                </h2>
                <blockquote className="border-l-2 border-cyanx/50 pl-6">
                  <p className="max-w-xl whitespace-pre-line font-serif text-2xl font-light italic leading-relaxed text-ink/90">
                    {book.excerpt}
                  </p>
                  <footer className="mt-4 font-mono text-[10px] uppercase tracking-[0.22em] text-mute2">
                    {book.excerpt_source}
                  </footer>
                </blockquote>
                {book.excerpt_link && (
                  <a
                    href={book.excerpt_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={T.booksySampleLink(book.slug)}
                    className="group mt-6 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-dim transition-colors hover:text-cyanx"
                  >
                    Continue reading the free sample
                    <ExternalLink size={13} className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                  </a>
                )}
              </div>
            )}

            <div className="mt-12 grid grid-cols-2 gap-4 border-t border-white/[0.07] pt-10 font-mono text-[11px] uppercase tracking-[0.18em] text-mute2">
              <div>
                <p className="text-mute2">Publisher</p>
                <p className="mt-1.5 text-dim">{book.publisher}</p>
              </div>
              <div>
                <p className="text-mute2">Length</p>
                <p className="mt-1.5 text-dim">{book.pages} pages</p>
              </div>
              <div>
                <p className="text-mute2">Formats</p>
                <p className="mt-1.5 text-dim">{book.formats}</p>
              </div>
              <div>
                <p className="text-mute2">Delivery</p>
                <p className="mt-1.5 text-dim">Instant via Amazon</p>
              </div>
            </div>
          </FadeUp>
        </div>
      </div>

      {book.slug === "transhuman" && (
        <div className="mt-16 border-t border-white/[0.07] pt-12">
          <h2 className="mb-9 font-mono text-[11px] uppercase tracking-[0.3em] text-cyanx">
            Watch the trailer
          </h2>
          <Trailer testid="trailer-video-detail" />
        </div>
      )}

      {others.length > 0 && (
        <div className="mt-28 border-t border-white/[0.07] pt-16">
          <Eyebrow className="mb-10">Continue the trilogy</Eyebrow>
          <div className="grid gap-10 sm:grid-cols-2">
            {others.map((b, i) => (
              <FadeUp key={b.slug} delay={i * 0.1} testid={T.bookCard(b.slug)}>
                <Link
                  to={`/books/${b.slug}`}
                  className="group flex items-center gap-7 rounded-2xl border border-white/[0.07] bg-fog/50 p-6 transition-all duration-300 hover:-translate-y-1 hover:border-cyanx/40"
                  data-testid={T.bookLink(b.slug)}
                >
                  <img
                    src={b.cover}
                    alt={`${b.title} — cover art`}
                    className="h-32 w-[86px] shrink-0 object-cover shadow-lg ring-1 ring-white/10"
                  />
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-mute2">
                      Book {String(b.series_order).padStart(2, "0")} — {b.year}
                    </p>
                    <h3 className="mt-1.5 font-serif text-2xl font-light text-ink transition-colors group-hover:text-cyanx">
                      {b.title}
                    </h3>
                    <p className="mt-1.5 font-mono text-[11px] tracking-[0.15em] text-cyanx">{b.price_label}</p>
                  </div>
                </Link>
              </FadeUp>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
